import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, MousePointerClick, Eye, Monitor, Globe2, Share2, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];
const regionName = (() => {
  try { const d = new Intl.DisplayNames(["en"], { type: "region" }); return (c) => (c === "Unknown" ? "Unknown" : d.of(c) || c); }
  catch { return (c) => c; }
})();

export function Bars({ title, icon: Icon, items, unit, fmt = (x) => x, testid }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="card-surface rounded-xl p-5" data-testid={testid}>
      <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Icon size={16} /> {title}</h3>
      {!items.length ? <p className="text-sm text-white/40">No data yet.</p> : (
        <div className="space-y-2.5">
          {items.map((i) => (
            <div key={i.name}>
              <div className="flex justify-between gap-3 text-sm mb-1"><span className="text-white/75 truncate">{fmt(i.name)}</span><span className="text-white/40 shrink-0">{i.count.toLocaleString()} {unit}</span></div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-[hsl(var(--primary))]" style={{ width: `${(i.count / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-surface rounded-xl p-5" data-testid={testid}>
      <Icon size={18} className="text-[hsl(var(--primary))]" />
      <div className="mt-3 text-2xl font-bold font-head text-white">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="text-xs text-white/50 mt-1">{label}</div>
    </div>
  );
}

export default function TrafficPanel() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/traffic", { params: { days } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [days]);
  return (
    <section className="mt-5" data-testid="traffic-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-white">Visitors <span className="text-xs font-normal text-white/40">· counted after the visitor accepts cookies</span></h2>
        <div className="flex gap-1.5">{RANGES.map(([v, l]) => (
          <button key={v} onClick={() => setDays(v)} data-testid={`traffic-range-${v}`} className={`px-3 py-1.5 rounded-full text-xs font-bold ${days === v ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-white/5 text-white/60 hover:text-white"}`}>{l}</button>
        ))}</div>
      </div>
      {d === null ? <Loader2 className="mt-6 animate-spin text-white/30" /> : d === false ? <p className="mt-4 text-sm text-red-400">Could not load traffic data.</p> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <Kpi icon={Users} label="Unique visitors" value={d.totals.visitors} testid="traffic-visitors" />
            <Kpi icon={MousePointerClick} label="Sessions (visits)" value={d.totals.sessions} testid="traffic-sessions" />
            <Kpi icon={Eye} label="Page views" value={d.totals.views} testid="traffic-views" />
          </div>
          <div className="card-surface rounded-xl p-5 mt-4" data-testid="traffic-chart">
            <h3 className="font-bold text-white mb-4">Daily visits</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.daily} margin={{ left: -20, right: 8 }}>
                  <defs><linearGradient id="tv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={20} />
                  <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0d1117", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                  <Area type="monotone" dataKey="sessions" name="Sessions" stroke="hsl(var(--primary))" fill="url(#tv)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" name="Unique visitors" stroke="#60a5fa" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Bars title="Top pages" icon={FileText} items={d.pages} unit="views" testid="traffic-pages" />
            <Bars title="Traffic sources" icon={Share2} items={d.sources} unit="sessions" testid="traffic-sources" />
            <Bars title="Devices" icon={Monitor} items={d.devices} unit="visitors" testid="traffic-devices" />
            <Bars title="Countries" icon={Globe2} items={d.countries} unit="visitors" fmt={regionName} testid="traffic-countries" />
            {d.referrers.length > 0 && <Bars title="Top referring sites" icon={Share2} items={d.referrers} unit="sessions" testid="traffic-referrers" />}
          </div>
        </>
      )}
    </section>
  );
}
